import { Injectable, signal } from '@angular/core';
import { environment } from '@environment';
import { Spin } from '@shared/types/spin';
import { catchError, defer, from, map, tap, throwError } from 'rxjs';
import { luckyDaysTheme } from '../wheel-store/luckydays-theme';
import { EngineAuthenticationError, EngineClient } from './engine-client';
import { engineWinningSegments, mapEngineWheels } from './engine.adapter';
import { EngineRound, EngineSpinState, EngineWheel } from './engine.models';

@Injectable({ providedIn: 'root' })
export class EngineService {
  readonly error = signal('');
  readonly loginRequired = signal(false);
  readonly signingIn = signal(false);
  readonly acceptsLaunchToken = environment.name === 'local';
  readonly spinState = signal<EngineSpinState | null>(null);
  readonly wheels = signal<EngineWheel[]>([]);

  loginUrl(): string {
    return this.client().loginUrl(window.location.href);
  }

  async signInWithToken(token: string): Promise<void> {
    if (this.signingIn()) return;
    this.signingIn.set(true);
    try {
      const gameId =
        new URLSearchParams(window.location.search).get('gameId') ?? '';
      await this.client().authenticate(gameId, token);
      window.location.reload();
    } catch (error) {
      this.setError(error);
    } finally {
      this.signingIn.set(false);
    }
  }

  setError(error: unknown): void {
    this.loginRequired.set(error instanceof EngineAuthenticationError);
    this.error.set(
      error instanceof Error ? error.message : 'Unable to load the wheel.',
    );
  }

  private client(): EngineClient {
    if (!environment.engineApiUrl)
      throw new Error(
        'The board engine is not configured in this environment.',
      );
    const params = new URLSearchParams(window.location.search);
    const timezone =
      params.get('timezone') ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    new Intl.DateTimeFormat('en', { timeZone: timezone });
    return new EngineClient(
      new URL(environment.engineApiUrl, window.location.origin).href,
      timezone,
    );
  }

  load() {
    return defer(() => {
      const url = new URL(window.location.href);
      const gameId = url.searchParams.get('gameId') ?? '';
      const token = url.searchParams.get('token')?.trim() || undefined;
      if (url.searchParams.has('token')) {
        url.searchParams.delete('token');
        window.history.replaceState(window.history.state, '', url.href);
      }
      return from(this.client().load(gameId, token));
    }).pipe(
      map(({ response, config, labels }) => {
        if (!config.theme && config.themeKey !== 'luckydays')
          throw new Error('This wheel theme is not available yet.');
        this.spinState.set(response.startPosition);
        const wheels = mapEngineWheels(
          config,
          config.theme ?? luckyDaysTheme,
          labels,
        );
        this.wheels.set(wheels);
        return wheels[0].game;
      }),
    );
  }

  play() {
    return this.playRound().pipe(map((round) => round.spins[0].spin));
  }

  playRound() {
    return defer(() => {
      if (!this.wheels().length)
        throw new Error('Load the wheel before spinning.');
      return from(this.client().play());
    }).pipe(
      tap(({ nextRoll, rollsAvailable }) => {
        if (nextRoll !== undefined && rollsAvailable !== undefined)
          this.spinState.set({ nextRoll, rollsAvailable });
      }),
      map((response): EngineRound => {
        const uuid = crypto.randomUUID();
        const createdTs = new Date();
        return {
          spins: engineWinningSegments(this.wheels(), response).map(
            ({ configId, game, segment }) => ({
              configId,
              spin: {
                uuid,
                game,
                createdTs,
                segment,
                nextSpinInfo: {
                  allowedToSpin: true,
                  nextSpinInSeconds: 0,
                  startTs: Date.now(),
                  lastSpinTransaction: null,
                },
              } satisfies Spin,
            }),
          ),
          rewards: response.actions.filter(
            (action) =>
              (action.type === 'prize' || action.type === 'xp') && action.value,
          ),
        };
      }),
      catchError((error: unknown) => {
        if (error instanceof EngineAuthenticationError) this.setError(error);
        return throwError(() => error);
      }),
    );
  }

  dynamicWallets() {
    return defer(() => from(this.client().dynamicWallets())).pipe(
      catchError((error: unknown) => {
        if (error instanceof EngineAuthenticationError) this.setError(error);
        return throwError(() => error);
      }),
    );
  }
}
